// src/pages/epl/innovatech/components/steps/ResumenStep.jsx
import React, { useEffect } from 'react';
import { CheckCircle, Target, Award, Lightbulb } from 'lucide-react';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
  ? _eplConsole
  : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

/**
 * 📋 Componente Resumen - Lab 1: Lean Startup Bootcamp
 * 
 * @component
 * @description Muestra el resumen final del laboratorio con progreso y recomendaciones
 * @param {Function} onFinish - Callback para finalizar el laboratorio
 * @param {string} userHypothesis - Hipótesis del usuario
 * @param {Object} circuitData - Datos del circuito BML
 * @param {Object} quizResults - Resultados del quiz
 * @param {Function} getLabProgressPercentage - Función para obtener progreso
 * @param {Function} getValidatedQuizResults - Función para validar resultados quiz
 * @param {Object} labPersistence - Hook de persistencia del lab
 */
const ResumenStep = ({ 
  onFinish, 
  userHypothesis, 
  circuitData, 
  quizResults, 
  getLabProgressPercentage,
  getValidatedQuizResults,
  labPersistence 
}) => {

  // 💾 Marcar el paso de resumen como completado al cargar
  useEffect(() => {
    if (labPersistence) {
      console.log('[📋 RESUMEN] Marcando paso de resumen como completado');
      labPersistence.saveStepProgress('resumen', {
        approved: true,
        score: 100, // Resumen siempre aprobado
        formData: null,
        aiResponse: {
          summary: 'Laboratorio completado exitosamente',
          timestamp: new Date().toISOString()
        }
      });
    }
  }, [labPersistence]);
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-purple-800">
              ¡Laboratorio Completado!
            </h3>
            <p className="text-purple-600">
              Resumen de tu progreso Lean Startup
            </p>
          </div>
        </div>

        {/* Progreso general */}
        <div className="bg-white bg-opacity-60 p-6 rounded-lg mb-6">
          <h4 className="font-semibold text-purple-800 mb-4">Progreso del laboratorio</h4>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div 
              className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2 rounded-full transition-all duration-500" 
              style={{ width: `${getLabProgressPercentage()}%` }}
            ></div>
          </div>
          <p className="text-sm text-purple-600">
            {getLabProgressPercentage()}% completado - ¡Excelente trabajo!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white bg-opacity-60 p-6 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Tu hipótesis
            </h4>
            <p className="text-purple-700 text-sm bg-purple-100 p-3 rounded italic">
              "{userHypothesis || 'No definida'}"
            </p>
            {circuitData && (
              <div className="mt-3 text-xs text-purple-600">
                <p>✓ Circuito BML diseñado</p>
                <p>✓ Métricas identificadas: {circuitData.kpis?.length || 0}</p>
              </div>
            )}
          </div>

          <div className="bg-white bg-opacity-60 p-6 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Resultados del quiz
            </h4>
            {(() => {
              const validatedResults = getValidatedQuizResults();
              return (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-2xl font-bold ${
                      validatedResults?.score >= 70 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {validatedResults?.score || 0}%
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      validatedResults?.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {validatedResults?.passed ? 'APROBADO' : 'REPROBADO'}
                    </span>
                  </div>
                  <div className="text-xs text-purple-600 mb-2">
                    Preguntas respondidas: {validatedResults?.totalQuestions || 6}
                  </div>
                  {validatedResults?.warning && (
                    <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded flex items-center gap-1">
                      <span>⚠️</span>
                      <span>{validatedResults.warning}</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Resumen de fases completadas */}
        <div className="bg-white bg-opacity-60 p-6 rounded-lg mb-6">
          <h4 className="font-semibold text-purple-800 mb-4">Fases completadas</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-xs text-purple-600 font-medium">BUILD</p>
              <p className="text-xs text-purple-500">Hipótesis</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-xs text-purple-600 font-medium">MEASURE</p>
              <p className="text-xs text-purple-500">KPIs</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-xs text-purple-600 font-medium">LEARN</p>
              <p className="text-xs text-purple-500">Validación</p>
            </div>
            <div className="text-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${
                quizResults?.passed ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <CheckCircle className={`w-4 h-4 ${
                  quizResults?.passed ? 'text-green-600' : 'text-gray-400'
                }`} />
              </div>
              <p className="text-xs text-purple-600 font-medium">QUIZ</p>
              <p className="text-xs text-purple-500">Evaluación</p>
            </div>
          </div>
        </div>

        <div className="bg-white bg-opacity-60 p-6 rounded-lg mb-6">
          <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Próximos pasos recomendados
          </h4>
          <div className="space-y-3">
            {/* Paso 1: MVP */}
            <div className="border-l-4 border-purple-300 pl-4">
              <h5 className="font-medium text-purple-800 flex items-center gap-2">
                <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">1</span>
                Implementar MVP
              </h5>
              <p className="text-sm text-purple-600 mt-1">
                Construye la versión más simple de tu producto que te permita validar: "{userHypothesis?.substring(0, 60)}..."
              </p>
            </div>

            {/* Paso 2: Métricas */}
            <div className="border-l-4 border-indigo-300 pl-4">
              <h5 className="font-medium text-indigo-800 flex items-center gap-2">
                <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">2</span>
                Configurar medición
              </h5>
              <p className="text-sm text-indigo-600 mt-1">
                {circuitData?.kpis?.length > 0 
                  ? `Implementa el tracking de tus ${circuitData.kpis.length} KPIs identificados`
                  : 'Define e implementa métricas específicas para medir el éxito'
                }
              </p>
            </div>

            {/* Paso 3: Timeline */}
            <div className="border-l-4 border-green-300 pl-4">
              <h5 className="font-medium text-green-800 flex items-center gap-2">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-600">3</span>
                Planificar experimento
              </h5>
              <p className="text-sm text-green-600 mt-1">
                Establece un timeline de 2-4 semanas para tu primer ciclo Build-Measure-Learn
              </p>
            </div>

            {/* Recomendación basada en quiz */}
            {quizResults?.score < 85 && (
              <div className="border-l-4 border-amber-300 pl-4">
                <h5 className="font-medium text-amber-800 flex items-center gap-2">
                  <span className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-bold text-amber-600">💡</span>
                  Reforzar conocimientos
                </h5>
                <p className="text-sm text-amber-600 mt-1">
                  Considera revisar los conceptos de Lean Startup antes de implementar para maximizar tus posibilidades de éxito.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Información adicional y badge de completado */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800">¡Laboratorio completado!</p>
                <p className="text-sm text-green-600">
                  Has dominado los fundamentos de Lean Startup
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={onFinish}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Finalizar laboratorio
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumenStep;